<template>
  <section class="hero-section" id="home" aria-label="Hero section">
    <div class="background-elements">
      <div class="gradient-sphere"></div>
      <div class="grid-overlay"></div>
      <div class="floating-elements" aria-hidden="true">
        <div class="tech-circle"></div>
        <div class="tech-square"></div>
        <div class="tech-dots"></div>
      </div>
    </div>

    <div class="content-container">
      <div class="hero-content">
        <div class="brand-tag slide-in" data-delay="100">
          <div class="tag-dot"></div>
          <span>VisionLab Studio</span>
        </div>

        <h1 class="main-heading">
          <div class="heading-line fade-in" data-delay="300">
            Transforming Ideas into
          </div>
          <div class="heading-highlight fade-in" data-delay="500">
            Digital Solution
            <svg
              class="heading-accent"
              viewBox="0 0 100 100"
              width="80"
              height="80"
            >
              <circle cx="50" cy="50" r="40" class="accent-circle" />
            </svg>
          </div>
        </h1>

        <p class="description fade-in" data-delay="700">
          We create sophisticated digital solutions that empower businesses
          through innovative technology. From enterprise applications to custom
          development, we bring your vision to life.
        </p>

        <div class="cta-group fade-in" data-delay="900">
          <a href="#pricing" class="primary-button">
            <span class="button-content">
              Explore Solutions
              <svg class="button-icon" viewBox="0 0 24 24">
                <path d="M4 12h16m-6-6 6 6-6 6" />
              </svg>
            </span>
            <div class="button-background"></div>
          </a>

          <a href="#portfolio" class="secondary-button"> View Our Work </a>
        </div>

        <div class="features-grid fade-in" data-delay="1100">
          <div class="feature-item">
            <div class="feature-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
                <circle cx="12" cy="12" r="1" fill="currentColor" />
              </svg>
            </div>
            <span>Enterprise Solutions</span>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18" />
                <path d="M9 21V9" />
                <circle cx="15" cy="15" r="2" fill="currentColor" />
              </svg>
            </div>
            <span>Custom Development</span>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                <circle cx="13" cy="9" r="1" fill="currentColor" />
              </svg>
            </div>
            <span>High Performance</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { onMounted, onBeforeUnmount } from "vue";

export default {
  name: "HeroSection",
  setup() {
    let observer = null;

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const delay = entry.target.dataset.delay || 0;
          setTimeout(() => {
            entry.target.classList.add("visible");
          }, delay);
        }
      });
    };

    onMounted(() => {
      const elements = document.querySelectorAll(".fade-in, .slide-in");
      observer = new IntersectionObserver(handleIntersection, {
        threshold: 0.1,
        rootMargin: "20px",
      });

      elements.forEach((el) => observer.observe(el));

      // Добавляем обработчик движения мыши для эффекта параллакса
      const handleMouseMove = (e) => {
        const floatingElements = document.querySelectorAll(
          ".floating-elements > div"
        );
        const { clientX, clientY } = e;
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;

        floatingElements.forEach((element, index) => {
          const speed = 0.01 + index * 0.01;
          const x = (clientX - centerX) * speed;
          const y = (clientY - centerY) * speed;
          element.style.transform = `translate(${x}px, ${y}px)`;
        });
      };

      document.addEventListener("mousemove", handleMouseMove);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
      };
    });

    onBeforeUnmount(() => {
      if (observer) {
        observer.disconnect();
      }
    });
  },
};
</script>

<style lang="scss" scoped>
.hero-section {
  --primary-color: #6366f1;
  --accent-color: #818cf8;
  --text-primary: #ffffff;
  --text-secondary: #e2e8f0;

  min-height: 100vh;
  background-color: #000000;
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.background-elements {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.gradient-sphere {
  position: absolute;
  top: -20%;
  right: -10%;
  width: 80vh;
  height: 80vh;
  background: radial-gradient(
    circle,
    rgba(99, 102, 241, 0.2) 0%,
    rgba(99, 102, 241, 0.1) 40%,
    transparent 70%
  );
  border-radius: 50%;
}

.grid-overlay {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(#3d73ff08 1px, transparent 1px),
    linear-gradient(90deg, #3d73ff08 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: radial-gradient(circle at 50% 50%, black, transparent 80%);
  opacity: 0;
  animation: gridFade 1s ease forwards 0.3s;
}

.floating-elements {
  position: absolute;
  inset: 0;

  .tech-circle {
    position: absolute;
    top: 20%;
    right: 15%;
    width: 200px;
    height: 200px;
    border: 1px solid rgba(99, 102, 241, 0.2);
    border-radius: 50%;
    transition: transform 0.3s ease;
  }

  .tech-square {
    position: absolute;
    bottom: 25%;
    left: 10%;
    width: 150px;
    height: 150px;
    border: 1px solid rgba(99, 102, 241, 0.2);
    transform: rotate(45deg);
    transition: transform 0.3s ease;
  }

  .tech-dots {
    position: absolute;
    top: 30%;
    left: 20%;
    width: 100px;
    height: 100px;
    background-image: radial-gradient(
      circle,
      rgba(99, 102, 241, 0.2) 1px,
      transparent 1px
    );
    background-size: 10px 10px;
    transition: transform 0.3s ease;
  }
}

.content-container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

.hero-content {
  max-width: 900px;
  margin-left: 4rem;
}

.brand-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 100px;
  margin-bottom: 2rem;

  .tag-dot {
    width: 8px;
    height: 8px;
    background: var(--accent-color);
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  span {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-primary);
    letter-spacing: 0.05em;
  }
}

.main-heading {
  margin-bottom: 2rem;

  .heading-line {
    font-size: clamp(1.5rem, 3vw, 2rem);
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
  }

  .heading-highlight {
    position: relative;
    font-size: clamp(2.5rem, 5vw, 4rem);
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1.1;

    .heading-accent {
      position: absolute;
      top: -20px;
      right: -40px;
      z-index: -1;

      .accent-circle {
        fill: none;
        stroke: var(--accent-color);
        stroke-width: 2;
        stroke-dasharray: 251;
        stroke-dashoffset: 251;
        animation: drawCircle 2s ease forwards;
      }
    }
  }
}

.description {
  font-size: clamp(1rem, 1.5vw, 1.25rem);
  line-height: 1.7;
  color: var(--text-secondary);
  margin-bottom: 3rem;
  max-width: 650px;
}

.cta-group {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 4rem;
}

.primary-button {
  position: relative;
  display: inline-block;
  padding: 1rem 2rem;
  text-decoration: none;
  overflow: hidden;
  border-radius: 4px;

  .button-content {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--text-primary);
    z-index: 1;
    font-weight: 500;

    .button-icon {
      width: 20px;
      height: 20px;
      stroke: currentColor;
      stroke-width: 2;
      fill: none;
    }
  }

  .button-background {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      45deg,
      var(--primary-color),
      var(--accent-color)
    );
    transition: transform 0.3s ease;
  }

  &:hover .button-background {
    transform: scale(1.05);
  }
}

.secondary-button {
  display: inline-flex;
  align-items: center;
  padding: 1rem 2rem;
  color: var(--text-primary);
  text-decoration: none;
  font-weight: 500;
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 4px;
  background: transparent;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    background: rgba(99, 102, 241, 0.1);
  }
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  .feature-item {
    display: flex;
    align-items: center;
    gap: 1rem;

    .feature-icon {
      width: 40px;
      height: 40px;
      padding: 0.5rem;
      background: rgba(99, 102, 241, 0.1);
      border-radius: 12px;

      svg {
        width: 100%;
        height: 100%;
        stroke: var(--accent-color);
        stroke-width: 2;
        fill: none;
      }
    }

    span {
      font-weight: 500;
      color: var(--text-secondary);
    }
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes drawCircle {
  to {
    stroke-dashoffset: 0;
  }
}

.fade-in {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-in {
  opacity: 0;
  transform: translateX(-20px);
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);

  &.visible {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-width: 768px) {
  .content-container {
    padding-top: 100px;
  }

  .hero-content {
    margin-left: 0;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .cta-group {
    flex-direction: column;
  }

  .floating-elements {
    display: none;
  }
}

@media print {
  .background-elements {
    display: none;
  }

  .fade-in,
  .slide-in {
    opacity: 1;
    transform: none;
  }
}
</style>
